import { useAuthenticator } from '@aws-amplify/ui-react';
import { useState, MouseEvent } from 'react';
import { useTheme } from 'styled-components';
import { User, UserRole, PatchUserRequest } from '../../../API/types/Users';
import useDriverThumbnail from '../../../theme/useDriverThumbnail';
import Modal from '../../../components/common/Modal/Modal';
import Tags from '../../../components/common/Tags';
import { Button } from '../../../components/ui/Button/Button';
import { ListItemWrapper } from '../../../components/ui/ListItemWrapper/ListItemWrapper';
import { getFormattedDate } from '../../../utils/dateUtils';
import { IconKeys } from '../../../components/common/Icon/IconKeys';
import useGetRole from '../../../API/hooks/useGetRole';
import { ThemeType } from '../../../theme/themeType';
import Select from '../../../components/common/Select/Select';
import usePatchUser from '../../../API/hooks/users/usePatchUser';
import DeleteModal from '../../../components/common/DeleteModal/DeleteModal';
import useDeleteUser from '../../../API/hooks/users/useDeleteUser';
import EditUser from '../Modals/EditUser/EditUser';
import { FeaturesManagement } from '../../../featuresControl';
import UserGroups from './UserGroups';
import { ROLE_MAP } from '../../../constants/UserRole';

type Props = {
  item?: User;
  lightVersion?: boolean;
};
function UsersItem({ item, lightVersion }: Props): JSX.Element {
  const theme = useTheme() as ThemeType;
  const userThumbnail = useDriverThumbnail();
  const {
    isFleetViewer, isFleetManager, isSupport, isAdmin, isFleetUser,
  } = useGetRole();
  const { user: { username, attributes }, signOut } = useAuthenticator();
  const canManage = !isFleetViewer;
  const myself = item?.email === attributes?.email;
  const [modalGroups, toggleModalGroups] = useState(false);
  const [modalEditState, toggleModalEditState] = useState(false);
  const [modalDeleteState, toggleModalDeleteState] = useState(false);
  const onSubmitSuccess = (): void => { };
  const onSubmitFail = (errorMessage: string): void => { };
  const { mutate: updateUser } = usePatchUser(onSubmitSuccess, onSubmitFail);
  const { mutate: deleteUser } = useDeleteUser(onSubmitSuccess, onSubmitFail);

  const {
    _id: id,
    createdAt,
    lastLoginAt,
    email,
    name,
    role,
    status,
    fleetId,
    fleet,
    groups,
  } = item!;

  const [userRole, setUserRole] = useState(role);

  const handleEditUser = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    toggleModalEditState(true);
  };

  const handleDeleteUser = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    toggleModalDeleteState(true);
  };

  // const handleChangeRole = (e: any): void => {
  //   if (canManage && item) {
  //     setUserRole(e.target.value);
  //     updateUser({
  //       fleetId: item?.fleetId,
  //       id: item?._id,
  //       email: item?.email,
  //       name: item?.name,
  //       role: e.target.value,
  //       adminRequest: isAdmin || isSupport,
  //     } as unknown as PatchUserRequest);
  //   }
  // };

  const canBeEdited = (userRole === 'fleetViewer' && !isFleetViewer)
    || (userRole === 'fleetManager' && !isFleetViewer && !isFleetManager)
    || (userRole === 'fleetAdmin' && !isFleetUser)
    || isSupport
    || isAdmin;

  const deleteUserAction = (): void => {
    if (canManage && item) {
      deleteUser({
        fleetId: item?.fleetId,
        id: item?._id,
        adminRequest: isAdmin || isSupport,
      });
    }
  };

  return (
    <>
      <ListItemWrapper noclick>
        <Tags.ItemOverview
          avatar={userThumbnail}
          avatarAlt="user"
          title={name}
          subtitle={email}
          isRoundImage
        />

        <Tags.LabeledIcon
          label={`Added on ${getFormattedDate(createdAt)}`}
          icon={IconKeys.DATE}
          flexible
        />
        <Tags.LabeledIcon
          label={lastLoginAt ? `Last active ${getFormattedDate(lastLoginAt)}` : 'Never logged in'}
          icon={IconKeys.PULSE}
          flexible
        />
        <Tags.LabeledIcon
          label={ROLE_MAP[userRole]}
          icon={IconKeys.USER}
          flexible
        />
        <div className="tags-container">
          {FeaturesManagement.features.groups && !isFleetViewer && (
            <Button
              height="26px"
              width="120px"
              padding="8px 8px"
              background={theme.colors.outline}
              hoverBackground={theme.colors.inactive}
              onClick={(e) => toggleModalGroups(true)}
              disabled={userRole !== 'fleetViewer' || !item?._id}
            >
              Groups
            </Button>
          )}
          <Button
            height="26px"
            width="120px"
            padding="8px 8px"
            background={theme.colors.outline}
            hoverBackground={theme.colors.inactive}
            onClick={(e) => handleEditUser(e)}
            disabled={!(myself || canBeEdited)}
          >
            Edit
          </Button>
          <Button
            height="26px"
            width="120px"
            padding="8px 8px"
            background={theme.colors.outline}
            hoverBackground={theme.colors.inactive}
            onClick={(e) => handleDeleteUser(e)}
            disabled={myself || !canBeEdited}
          >
            Remove
          </Button>
        </div>
      </ListItemWrapper>
      {modalGroups && (
        <Modal
          title="User groups"
          state={modalGroups}
          toggleState={toggleModalGroups}
        >
          <UserGroups fleetId={fleetId} entityId={item?._id} onClose={() => toggleModalGroups(false)} />
        </Modal>
      )}
      {modalEditState && (
        <Modal
          title="Edit user"
          state={modalEditState}
          toggleState={toggleModalEditState}
        >
          <EditUser data={item} toggleModal={toggleModalEditState} />
        </Modal>
      )}
      {modalDeleteState && (
        <Modal
          title="Delete user"
          state={modalDeleteState}
          toggleState={toggleModalDeleteState}
          settings={{
            minWidth: '500px',
          }}
        >
          <DeleteModal
            title="Are you sure you want to delete this user?"
            onClose={() => toggleModalDeleteState(false)}
            onDelete={deleteUserAction}
          />
        </Modal>
      )}
    </>
  );
}

export default UsersItem;
